//=====================================================
//　G021：患者情報変更
//=====================================================

import { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AxiosResponse } from "axios";
import { BtnSubmit } from "../../common/BtnSubmit";
import { ContentsBox } from "../../common/ContentsBox";
import { ContentsTitle } from "../../common/ContentsTitle";
import { FormBox } from "../../common/FormBox";
import { MsgBoxErr } from '../../common/MsgBoxErr';
import { TxtChangePassword } from "../../common/TxtChangePassword";
import { TxtEmail } from "../../common/TxtEmail";
import { ChkVisitNotice } from "../../common/ChkVisitNotice";
import { ChkEndNotice } from "../../common/ChkEndNotice";
import { NotesMail } from "../../common/NotesMail";
import { BtnReturn } from "../../common/BtnReturn";
import { getApiDataWithToken, putApiDataWithToken } from "../../utils/CallApi";
import { useAuth } from "../../utils/UseAuth";
import { ConstMsg } from "../../utils/ConstMsg";
import { CtxPatientDetailInfo, CtxClinicInfo, CtxClinicSystemInfo, CtxSystemErr, CtxPatientInfo } from "../../utils/Contexts";
import { IFDataPatientDetailInfo, InitDataPatientDetailInfo } from "../../utils/UsePatientDetailInfo";
import { LoadingBox } from "../../common/LoadingBox";
import { LoginStatusType } from "../../utils/ConstCommon"

export function G021(){

    const [errmess, setErrmess] = useState('');
    const [data, setData] = useState<IFDataPatientDetailInfo>(Object.assign({}, InitDataPatientDetailInfo));
    const [loadingDone, setLoadingDone] = useState(false);

    const history = useHistory();
    const cserr = useContext(CtxSystemErr);
    const upd = useContext(CtxPatientDetailInfo);
    const uf = useForm({reValidateMode: "onSubmit"});

    const cci = useContext(CtxClinicInfo);
    const ccsi = useContext(CtxClinicSystemInfo);
    const cpi = useContext(CtxPatientInfo);
    const ua = useAuth();
   
    const token = ua.getToken();
    const patientNo = ua.getPatientNo();
    const clinicId = cci.data.id;


    // ブラウザバックで表示させない処理
    useEffect(function(){
        //タイトル追加処理
        if(cci.data.name){
            document.title = cci.data.name + " | Web予約 - 患者情報変更";
        }else{
            document.title = process.env.REACT_APP_DEFAULT_CLININC_NAME + " | Web予約 - 患者情報変更";
        }

        // マウントフラグ
        let isMounted = true;
        
        // アンマウント時はstate(history)を更新しないようにする
        if(isMounted){

            // 未ログインの場合WEB予約トップへ遷移する
            if(!ua.isLoggedIn()) {
                history.push("/");
            }

            // 変更完了データの場合、マイページ、トップへ遷移
            if(upd.data.done){
                if(ua.isLoggedIn()){
                    history.push('/mypage');
                }
            }
        }

        // cleanup関数(Unmount時の処理)
        return () => {
            // マウントオフ
            isMounted = false;
        }
        // アンマウント時にのみ一回だけ実行したいため第2引数指定なし。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    //バリデーション通過時処理
    function FormSubmit(){
        if( ua.checkExpiresIn() !== LoginStatusType.Login ){
            //ログアウト、ログイン期限切れ
            ua.logout(function(){history.push("/")});
        }else{
            setLoadingDone(false);
            setErrmess('');

            // 入力値の設定
            let newData:IFDataPatientDetailInfo = Object.assign({}, InitDataPatientDetailInfo);
            newData.email = uf.getValues("email") ?? "";
            newData.password = uf.getValues("login-password");
            newData.visitNotificationMail = uf.getValues("visit-notice-mail");
            newData.visitNotificationSms = uf.getValues("visit-notice-sms");
            newData.endNotificationMail = uf.getValues("finish-notice-mail");
            newData.endNotificationSms = uf.getValues("finish-notice-sms");

            // 患者情報更新API呼び出し
            let updateApiUrl = `/auth/c/${clinicId}/patients/${patientNo}`;
            putApiDataWithToken(
                updateApiUrl,
                newData,
                token,
                function(res:AxiosResponse<any>){
                    setLoadingDone(true);
                    if (res?.data) {
                        if( res.status === 200 ) {
                            //正常ケース
                            newData.done = true;
                            newData.patientId = data.patientId;
                            newData.firstName = data.firstName;
                            newData.lastName = data.lastName;
                            newData.firstNameKana = res.data.firstNameKana;
                            newData.lastNameKana = res.data.lastNameKana;
                            newData.postCode1 = res.data.postalCode1;
                            newData.postCode2 = res.data.postalCode2;
                            newData.address = res.data.address;
                            newData.tel = res.data.tel;
                            newData.telPrivate = res.data.telPrivate;
                            newData.birthday = res.data.birthday;

                            upd.update(newData);

                            //患者情報再取得
                            let errMess = cpi.getData(cci.data.id, patientNo);
                            if (errMess) {
                                cserr.setErrmess(errMess);
                            } else {
                                history.push("/mypage/user/complete");
                            }

                        } else if( res.status === 403 || res.status >= 500 ){
                            cserr.setErrmess(res.data.message);
                        } else {
                            cserr.setErrmess(ConstMsg.EG9990001);
                        }
                    } else {
                        cserr.setErrmess(ConstMsg.EG9990001);
                    }
                }
            );
        }
    }

    // 患者詳細情報の取得
    useEffect(function() {
        // マウントフラグ
        let isMounted = true;
        if( ua.isLoggedIn() ){

            let apiUrl = `/auth/c/${clinicId}/patients/${patientNo}/detail`;
        
            setErrmess('');
            getApiDataWithToken(
                apiUrl,
                {},
                token,
                function(res:AxiosResponse<any>) {
                    // アンマウント時はstateを更新しないようにする
                    if(isMounted) {
                        if (res?.data) {
                            if( res.status === 200 ) {

                                let data:IFDataPatientDetailInfo = Object.assign({}, InitDataPatientDetailInfo);
                        
                                //APIから取得した値格納
                                data.message = res.data.message;
                                data.patientId = res.data.patientId;
                                data.firstName = res.data.firstName;
                                data.lastName = res.data.lastName;
                                data.firstNameKana = res.data.firstNameKana;
                                data.lastNameKana = res.data.lastNameKana;
                                data.postCode1 = res.data.postalCode1;
                                data.postCode2 = res.data.postalCode2;
                                data.address = res.data.address;
                                data.tel = res.data.tel;
                                data.telPrivate = res.data.telPrivate;
                                data.birthday = res.data.birthday;
                                setData(data);
                                upd.update(data);

                                // Formの初期値設定
                                uf.setValue("email", res.data.email);
                                uf.setValue("login-password", res.data.password);
                                uf.setValue("login-password-confirm", res.data.password);
                                if(res.data.visitNotificationMail === 1) {
                                    uf.setValue("visit-notice-mail", 1);
                                }
                                if(res.data.visitNotificationSms === 1) {
                                    uf.setValue("visit-notice-sms", 1);
                                }
                                if(res.data.endNotificationMail === 1) {
                                    uf.setValue("finish-notice-mail", 1);
                                }
                                if(res.data.endNotificationSms === 1) {
                                    uf.setValue("finish-notice-sms", 1);
                                }
                            } else if( res.status === 403 || res.status === 404 || res.status >= 500 ){
                                cserr.setErrmess(res.data.message);
                            } else {
                                cserr.setErrmess(ConstMsg.EG9990001);
                            }
                        } else {
                            cserr.setErrmess(ConstMsg.EG9990001);
                        }
                        setLoadingDone(true);
                    }
                }
            );
        }
        // cleanup関数(Unmount時の処理)
        return() => {
            // マウントオフ
            isMounted = false;
        }
        // エラーメッセージを更新するため、cserrは第2引数から除外する
        // 指定すると無限ループするので、ua,uf,updは第2引数から除外する
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[token, clinicId, patientNo]
    );

    return (
        <>
            <ContentsBox>
                <ContentsTitle title="患者様情報を入力してください" />
                {errmess && <MsgBoxErr text={errmess} />}
                <LoadingBox overlay={true} loadingDone={loadingDone}>
                    {data.patientId && 
                        <div className="form-box">
                            <ul className="patient-data">
                                <li>
                                    <div className="patient-data-title">お名前</div>
                                    <div className="patient-data-content">{data.lastName}　{data.firstName}</div>
                                </li>
                                <li>
                                    <div className="patient-data-title">お名前（ふりがな）</div>
                                    <div className="patient-data-content">{data.lastNameKana}　{data.firstNameKana}</div>
                                </li>
                                {ccsi.data.display.birthday &&
                                    <li>
                                        <div className="patient-data-title">生年月日</div>
                                        <div className="patient-data-content">{data.birthday}</div>
                                    </li>
                                }
                                <li>
                                    <div className="patient-data-title">郵便番号</div>
                                    {data.postCode1 && data.postCode2 ?    
                                        <div className="patient-data-content">{data.postCode1}-{data.postCode2}</div>
                                    :
                                        <div className="patient-data-content"></div>
                                    }
                                </li>
                                <li>
                                    <div className="patient-data-title">住所</div>
                                    <div className="patient-data-content">{data.address}</div>
                                </li>
                                <li>
                                    <div className="patient-data-title">電話番号（自宅）</div>
                                    <div className="patient-data-content">{data.tel}</div>
                                </li>
                                <li>
                                    <div className="patient-data-title">電話番号（携帯）</div>
                                    <div className="patient-data-content">{data.telPrivate}</div>
                                </li>
                            </ul>
                            <FormBox useForm={uf} successFunc={FormSubmit} className="patient-data">
                                <TxtChangePassword />
                                <TxtEmail name="email" />
                                <ChkVisitNotice />
                                <ChkEndNotice />
                                <NotesMail />
                                <BtnSubmit title="変更する" />
                            </FormBox>
                        </div>
                    }
                </LoadingBox>
            </ContentsBox>

            <BtnReturn />
        </>
    );

}